import React from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

/**
 * Error Boundary component to catch JavaScript errors in child components
 * Prevents the entire app from crashing when a component fails
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log error to console in development
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({ errorInfo });

        // You could also log to an error reporting service here
        // e.g., Sentry, LogRocket, Firebase Crashlytics
    }

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    handleRetry = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
    };

    render() {
        if (this.state.hasError) {
            // Render fallback UI
            return (
                <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 max-w-md w-full p-6 text-center">
                        {/* Icon */}
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
                        </div>

                        {/* Title */}
                        <h1 className="text-xl font-black text-slate-900 mb-2">
                            Something went wrong
                        </h1>

                        {/* Description */}
                        <p className="text-sm text-slate-600 mb-6">
                            We encountered an unexpected error. Don't worry, your data is safe.
                            Try refreshing the page or going back to the home screen.
                        </p>

                        {/* Error details (collapsed by default) */}
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="mb-6 text-left bg-slate-100 rounded-lg p-3">
                                <summary className="text-xs font-bold text-slate-700 cursor-pointer">
                                    Error Details (Dev Only)
                                </summary>
                                <pre className="mt-2 text-[10px] text-red-600 overflow-auto max-h-32 whitespace-pre-wrap">
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}

                        {/* Action Buttons */}
                        <div className="space-y-3">
                            <button
                                onClick={this.handleRetry}
                                className="w-full flex items-center justify-center space-x-2 bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 px-4 rounded-xl transition-colors"
                            >
                                <ArrowPathIcon className="w-5 h-5" />
                                <span>Try Again</span>
                            </button>

                            <div className="flex space-x-3">
                                <button
                                    onClick={this.handleReload}
                                    className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2 px-4 rounded-xl transition-colors text-sm"
                                >
                                    Refresh Page
                                </button>
                                <button
                                    onClick={this.handleGoHome}
                                    className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-2 px-4 rounded-xl transition-colors text-sm"
                                >
                                    Go Home
                                </button>
                            </div>
                        </div>

                        {/* Support link */}
                        <p className="mt-6 text-xs text-slate-400">
                            If this keeps happening, please{' '}
                            <a href="/contact" className="text-blue-500 hover:underline">
                                contact support
                            </a>
                        </p>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
